import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConsultantContactSuccessModalComponent } from '@consultant/consultant-contact-success-modal/consultant-contact-success-modal.component';
import { DefaultConsultantImagePlaceholderSrc } from '@core/constants/image-placeholders';
import { SuccessfulStatus } from '@core/enums/successful-status.enum';
import { ModalConfig } from '@core/models/modal-config.model';
import { AppState } from '@core/store';
import { selectCart } from '@core/store/cart';
import { OrderLine } from '@core/store/cart/cart-state-models';
import { selectPersonalInfo } from '@core/store/checkout';
import { PersonalInfo } from '@core/store/checkout/checkout-state-models';
import { Consultant } from '@core/store/consultant/consultant-state-models';
import { ContactWholesaleConsulant } from '@core/store/consultant/consultant.actions';
import { OrderDetails } from '@core/store/order/order-state-models';
import { environment } from '@env';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { SelectOption } from '@shared/components/select/select.component';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
@Component({
  selector: 'app-consultant-wholesale-modal',
  templateUrl: './consultant-wholesale-modal.component.html',
  styleUrls: ['./consultant-wholesale-modal.component.scss'],
})
export class ConsultantWholesaleModalComponent implements AfterViewInit, OnDestroy {
  personalInfo$: Observable<PersonalInfo>;

  @Input() defaultValue: any = $localize`Select`;
  @Input() consultant: Consultant;
  @Input() value: string;
  orderDetails$: Observable<OrderDetails>;
  orderLines: OrderLine[];
  showX: boolean = true;
  recaptchaToken: string | null = null;
  sitekey = environment.recaptchaSiteKeyV2;
  readonly imagePlaceholder = DefaultConsultantImagePlaceholderSrc;

  contactTimings: SelectOption[] = [
    { name: $localize`Morning`, value: $localize`Morning` },
    { name: $localize`Afternoon`, value: $localize`Afternoon` },
    { name: $localize`Evening`, value: $localize`Evening` },
    { name: $localize`Anytime`, value: $localize`Anytime` },
  ];

  orderLineColumns = [
    { title: $localize`SKU` },
    { title: $localize`Qty` },
    { title: $localize`Product Name` },
  ];

  readonly FirstName = 'firstName';
  readonly LastName = 'lastName';
  readonly EmailAddress = 'emailAddress';
  readonly MobilePhone = 'mobilePhone';
  readonly ContactTime = 'contactTime';
  readonly message = 'message';
  readonly recaptchaC = 'recaptcha';
  readonly contactMethod = 'contactMethod';

  modalConfig: ModalConfig = {
    hideCloseButton: true,
    hideDismissButton: true,
    hideHeader: false,
    title: $localize`Contact Consultant`,
    onDismiss: this.modalDone.bind(this),
    ngbModalOptions: {
      size: 'md',
      modalDialogClass: 'modal-contact',
    },
  };

  recaptchaResponse: string | null = null;
  public contactWholesaleFormGroup: FormGroup;
  @Output() consultantMessageSent: EventEmitter<Consultant> = new EventEmitter<Consultant>();
  @Output() consultantMessageNotSent: EventEmitter<void> = new EventEmitter<void>();

  reCaptchaValidation$: Observable<SuccessfulStatus>;

  @ViewChild('modal') private modalContent: TemplateRef<ConsultantWholesaleModalComponent>;
  @ViewChild('consModal') private consModal: ModalComponent;
  @ViewChild('consContactSuccess')
  private consContactSuccessModal: ConsultantContactSuccessModalComponent;
  private subscriptions: Subscription = new Subscription();
  private modalRef: NgbModalRef;
  private size: 'xl';

  constructor(
    private store$: Store<AppState>,
    private fb: FormBuilder,
  ) {}

  get recaptcha() {
    return this.contactWholesaleFormGroup.get('recaptcha');
  }

  onExpired(): void {
    this.recaptcha?.setValue('');
  }

  SucceededDone() {
    this.consultantMessageSent.emit(this.consultant);
    this.consModal.close();
  }

  validContactMessage(): boolean {
    return this.contactWholesaleFormGroup.get(this.message).valid;
  }

  resolved(captchaResponse: string) {
    this.recaptcha?.setValue(captchaResponse);
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      combineLatest([this.store$.select(selectPersonalInfo), this.store$.select(selectCart)])
        .pipe(
          filter(([, personalInfo]) => !!personalInfo),
          take(1),
        )
        .subscribe(([personalInfo, orderDetails]) => {
          this.contactWholesaleFormGroup = this.fb.group({
            [this.FirstName]: personalInfo?.billingCustomer?.firstName,
            [this.LastName]: personalInfo?.billingCustomer?.lastName,
            [this.MobilePhone]: personalInfo?.billingCustomer?.phoneNumber,
            [this.EmailAddress]: personalInfo?.billingCustomer?.email,
            [this.message]: ['', Validators.required],
            [this.ContactTime]: $localize`Morning`,
            [this.recaptchaC]: ['', Validators.required],
            [this.contactMethod]: [],
          });
          this.orderLines = orderDetails.orderLines;
        }),
    );
  }

  open(payload: any = null) {
    this.consultant = payload;
    this.consModal.open();
  }

  submit(): void {
    this.store$.dispatch(
      ContactWholesaleConsulant({
        payload: {
          firstName: this.contactWholesaleFormGroup.value[this.FirstName],
          lastName: this.contactWholesaleFormGroup.value[this.LastName],
          email: this.contactWholesaleFormGroup.value[this.EmailAddress],
          phoneNumber: this.contactWholesaleFormGroup.value[this.MobilePhone],
          contactTime: this.contactWholesaleFormGroup.value[this.ContactTime],
          message: this.contactWholesaleFormGroup.value[this.message],
          consultantEmail: this.consultant.email,
          orderLines: this.orderLines,
        },
      }),
    );
    this.consContactSuccessModal.open();
  }

  public SendMessage(): void {
    if (this.contactWholesaleFormGroup.invalid) {
      return;
    }

    if (this.recaptcha) {
      this.submit();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  close() {
    this.consultantMessageSent.emit();
    this.consModal.close();
  }

  closedNoMessage() {
    this.consultantMessageNotSent.emit();
    this.consModal.close();
  }

  private modalDone() {
    this.consultantMessageNotSent.emit();
    this.consModal.close();
  }
}
